/* eslint-disable max-lines */
import { BottomSheet, Text, type UseModalState } from "@clipboard-health/ui-components";
import { Box } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { type ReactElement, type RefObject, useEffect } from "react";

import { type LogEntry as LogEntryType, type LogLevel } from "../types";
import { LogEntry } from "./LogEntry";
import { LogFilters } from "./LogFilters";

interface ConsoleModalProps {
  logs: LogEntryType[];
  filteredLogs: LogEntryType[];
  logCounts: Record<LogLevel, number>;
  activeFilters: Set<LogLevel>;
  logContainerRef: RefObject<HTMLDivElement>;
  modalState: UseModalState;
  onClearLogs: () => void;
  onClearFilters: () => void;
  onToggleFilter: (level: LogLevel) => void;
}

export function ConsoleModal({
  logs,
  filteredLogs,
  logCounts,
  activeFilters,
  logContainerRef,
  modalState,
  onClearLogs,
  onClearFilters,
  onToggleFilter,
}: ConsoleModalProps): ReactElement {
  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    if (logContainerRef.current) {
      requestAnimationFrame(() => {
        const container = logContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  }, [logs.length, logContainerRef]);

  return (
    <BottomSheet modalState={modalState}>
      <Box
        sx={{
          height: "85vh",
          background: "linear-gradient(145deg, #1a1a1a 0%, #2d2d2d 100%)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 12px 8px",
            background:
              "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
              }}
            >
              <img
                src="/assets/logo/bunny.png"
                alt="CBH Console"
                style={{
                  width: "70%",
                  height: "70%",
                  borderRadius: "50%",
                }}
              />
            </Box>
            <Box>
              <Text
                sx={{
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "1.1rem",
                  lineHeight: 1.2,
                }}
              >
                Mobile Console
              </Text>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            {activeFilters.size > 0 && (
              <Button
                size="small"
                variant="contained"
                sx={{
                  minWidth: "auto",
                  fontSize: "0.7rem",
                  px: 1,
                  py: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 500,
                  backgroundColor: "rgba(255, 171, 64, 0.25)",
                  borderColor: "#ffab40",
                  color: "#ffab40",
                  "&:hover": {
                    backgroundColor: "rgba(255, 171, 64, 0.35)",
                  },
                  "&:active": {
                    backgroundColor: "rgba(255, 171, 64, 0.25)",
                  },
                }}
                onClick={onClearFilters}
              >
                Clear Filters
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              sx={{
                minWidth: "auto",
                fontSize: "0.7rem",
                px: 3,
                py: 1,
                borderRadius: 2,
                fontWeight: 500,
                backgroundColor: "rgba(100, 181, 246, 0.25)",
                borderColor: "#64b5f6",
                color: "#64b5f6",
                "&:hover": {
                  backgroundColor: "rgba(100, 181, 246, 0.35)",
                },
                "&:active": {
                  backgroundColor: "rgba(100, 181, 246, 0.25)",
                },
              }}
              onClick={onClearLogs}
            >
              Clear All
            </Button>
            <IconButton
              variant="outlined"
              size="small"
              iconType="close"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "#ffffff",
              }}
              onClick={() => {
                modalState.closeModal();
              }}
            />
          </Box>
        </Box>

        {/* Log summary */}
        {logs.length > 0 && (
          <LogFilters
            logCounts={logCounts}
            activeFilters={activeFilters}
            onToggleFilter={onToggleFilter}
          />
        )}

        {/* Logs */}
        <Box
          ref={logContainerRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
            paddingBottom: "env(safe-area-inset-bottom, 16px)",
            // Custom scrollbar styling
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              borderRadius: 3,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              borderRadius: 3,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.5)",
              },
            },
          }}
        >
          {filteredLogs.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "rgba(255, 255, 255, 0.6)",
                textAlign: "center",
                gap: 3,
              }}
            >
              <Text sx={{ fontSize: "1.2rem", fontWeight: 600, color: "#ffffff" }}>
                {logs.length === 0 ? "Console Ready" : "No Matching Logs"}
              </Text>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Text sx={{ fontSize: "0.95rem", color: "rgba(255, 255, 255, 0.8)" }}>
                  {logs.length === 0
                    ? "Waiting for console output..."
                    : "Try adjusting your filters above"}
                </Text>
                {logs.length === 0 && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#4caf50",
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%": { opacity: 0.5 },
                        "50%": { opacity: 1 },
                        "100%": { opacity: 0.5 },
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          ) : (
            filteredLogs.map((log, index) => (
              <Box
                key={log.id}
                sx={{
                  animation: `slideInUp 0.3s ease-out ${index * 0.05}s both`,
                  "@keyframes slideInUp": {
                    "0%": {
                      opacity: 0,
                      transform: "translateY(20px)",
                    },
                    "100%": {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                }}
              >
                <LogEntry log={log} />
              </Box>
            ))
          )}
        </Box>
      </Box>
    </BottomSheet>
  );
}
/* eslint-enable max-lines */
